import { useEffect, useMemo, useState } from "react";
import CustomDataTable from "../../../components/CustomDataTable"
import { AlertService } from "../../../services/Alert";
import ModalAddSubAccount from "./ModalAddSubAccount";
import { useLocation } from "react-router-dom";
import { DateUtil } from "../../../utils/date";
import { SubAccountService } from "../../../services/SubaccountService";
import { useCurrentAccount } from "../../../hooks/useCurrentAccount";
import { useTranslation } from "react-i18next";
import useRequest from "../../../hooks/useRequest";

function SubaccountsPartial() {

    const { t } = useTranslation();
    const {call, isRequesting} = useRequest();
    const { account } = useCurrentAccount();

    const [subaccounts, setSubaccounts] = useState([])
    const [showModal, setShowModal] = useState(false);

    const columns = useMemo(() => [
        {
            name: t("config.subaccount.id"),
            sortable: true,
            selector: row => row.id,
        },
        {
            name: t("config.subaccount.name"),
            sortable: true,
            selector: row => row.name,
        },
        {
            name: t("config.subaccount.description"),
            sortable: true,
            selector: row => row.description,
        },
        {
            name: t("config.subaccount.last-update"),
            sortable: true,
            selector: row => <p className="text-nowrap mb-0">{DateUtil.format(row.last_update)}</p>,
        },
        {
            name: t("config.subaccount.exclusive"),
            sortable: true,
            cell: (row) => {
                return (
                    row.exclusive === 1 ?
                        (
                            <span className="badge bg-soft-info text-info">
                                <span className="legend-indicator bg-info"></span> {t("config.subaccount.active")}
                            </span>
                        ) :
                        (
                            <span className="badge bg-soft-danger text-danger">
                                <span className="legend-indicator bg-danger"></span> {t("config.subaccount.inactive")}
                            </span>
                        )
                )
            }

        },
        {
            name: t("config.subaccount.status"),
            sortable: true,
            cell: (row) => {
                return (
                    row.status === 1 ?
                        (
                            <span className="badge bg-soft-info text-info">
                                <span className="legend-indicator bg-info"></span> {t("config.subaccount.active")}
                            </span>
                        ) :
                        (
                            <span className="badge bg-soft-danger text-danger">
                                <span className="legend-indicator bg-danger"></span> {t("config.subaccount.inactive")}
                            </span>
                        )
                )
            }
        },

        {
            name: t("config.subaccount.actions"),
            sortable: false,
            center: true,
            cell: (row) => {
                return (
                    <div className="btn-group" role="group">
                        <button className="btn bg-soft-info-alternative btn-sm text-white-dark" onClick={() => changeStatus(row.id, row.status)}>
                            {
                                row.status === 1 ? t("config.subaccount.disabled"): t("config.subaccount.enable")
                            }
                        </button>

                        <div className="btn-group">
                            <button type="button" className="btn bg-soft-info-alternative btn-icon btn-sm dropdown-toggle dropdown-toggle-empty" data-bs-toggle="dropdown" aria-expanded="false"></button>

                            <div className="dropdown-menu dropdown-menu-end mt-1" >
                                <button className="dropdown-item" onClick={() => changeExclusive(row.id, row.exclusive)}>
                                    {
                                        row.exclusive === 1 ? t("config.subaccount.disable-exclusive")
                                            : t("config.subaccount.enable-exclusive")
                                    }
                                </button>
                            </div>
                        </div>

                    </div>
                )
            }
        },
    ], []);

    const changeStatus = async (id, status) => {
        const isConfirmed = await AlertService.warning('Aviso', '¿Estás seguro de querer cambiar el estatus de la subcuenta?', async () => {
            try {
                await SubAccountService.changeStatusSubaccount(id, status? 0: 1);
                await getSubaccounts()
            } catch (error) {
                console.error(error);
                AlertService.error('¡Error!', 'Ocurrió un error al cambiar el estatus de la subcuenta, intenta nuevamente')
            }
        });

        if (isConfirmed) {
            AlertService.success('¡Éxito!', "Se cambio el estatus correctamente a la subcuenta");
        }
    }

    const changeExclusive = async (id, exclusive) => {
        const message = !exclusive ? 
            `Al activar la asignación exclusiva, las bases de clientes, con esta subcuenta, solo se le asignarán agentes que pertenezcan a la misma subcuenta.`:
            `¿Estás seguro de querer desactivar asignación exclusiva?`;

        const isConfirmed = await AlertService.warning('Aviso', message, async () => {
            try {

                await SubAccountService.changeExclusiveSubAccount(id, exclusive ? 0: 1);
                await getSubaccounts()
            } catch (error) {
                console.error(error);
                AlertService.error('¡Error!', 'Ocurrió un error al cambiar la asignación exclusiva, intenta nuevamente')
            }
        });

        if (isConfirmed) {
            AlertService.success('¡Éxito!', 'Se cambio el estatus correctamente', 'Ok');
        }
    }

    const onCloseModal = async(reload: boolean) => {
        setShowModal(false)

        if(reload) {
            await getSubaccounts();
        }
        
    }

    const getSubaccounts = async () => {
        const {data} = await call(() => SubAccountService.getSubaccounts(account.id));
        setSubaccounts(data.sub_accounts)
    }

    useEffect(() => {
        if (account) {
            getSubaccounts()
        }
    }, [account])


    return (
        <>
            <div className="card">

                <div className="card-header d-flex align-items-center">
                    <div className="me-3">
                        <h2 className="card-title h4">{t("config.subaccount.sub-title")}</h2>
                        <p className="small mb-0">
                            {t("config.subaccount.sub-p")}
                        </p>
                    </div>

                    <div className="col-sm-auto">
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={() => setShowModal(true)}
                        >
                            <i className="bi-plus-lg me-1"></i>
                            {t("config.subaccount.add-btn")}
                        </button>
                    </div>
                </div>

                <div className="card-body">
                    <CustomDataTable
                        columns={columns}
                        data={subaccounts}
                        hideAccountsFilter
                        isLoading={isRequesting}
                        filter={(row, term) => {
                            if (!term) return true;

                            return (
                                `${row.description}`.match(new RegExp(`${term}`, 'i')) ||
                                `${row.name}`.match(new RegExp(`${term}`, 'i'))
                            )
                        }}
                    />

                </div>
            </div>

            <ModalAddSubAccount
                show={showModal}
                accountID={account.id}
                onRequestClose={onCloseModal}
            />

        </>
    )
}
export default SubaccountsPartial